import { useEffect, useRef } from "react";
import { HorizontalRail } from "../../../design-v2";

/*
 * Rep roulette (EP blueprint §6.7, canon §13.2): one option per rep count of
 * the API's domain (`performance.repCounts`, 1–30, gaps included). Options
 * without a drawn point are dimmed and inert. The small numeral is the
 * session count the API gives for that rep count. Arrow keys move to the next
 * selectable option; nothing here pools or converts rep counts.
 */
export default function RepRoulette({ repCounts, value, onChange }) {
  const buttons = useRef(new Map());
  const selectable = repCounts.filter((entry) => entry.selectable).map((entry) => entry.reps);
  // The one tab stop: the selected option, else the first selectable one.
  const tabStop = selectable.includes(value) ? value : selectable[0];

  // Centre the selected option. The rail's own "nearest" scroll lands on the
  // closest centre-snap position, which can leave the wider selected option
  // clipped at the edge; scrolling only the rail also never moves the page.
  useEffect(() => {
    const node = buttons.current.get(value);
    const container = node?.parentElement;
    if (!node || !container || typeof container.scrollTo !== "function") return;
    const offset = node.getBoundingClientRect().left - container.getBoundingClientRect().left;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    container.scrollTo({
      left: container.scrollLeft + offset - (container.clientWidth - node.offsetWidth) / 2,
      behavior: reduced ? "auto" : "smooth",
    });
  }, [value]);

  // From a dimmed option (focusable by mouse, never selectable): the nearest
  // selectable option in rail order in the requested direction, if any.
  function nearestSelectable(from, direction) {
    const order = repCounts.map((entry) => entry.reps);
    for (let index = order.indexOf(from) + direction; index >= 0 && index < order.length; index += direction) {
      if (selectable.includes(order[index])) return order[index];
    }
    return undefined;
  }

  function move(from, direction) {
    const index = selectable.indexOf(from);
    const next = direction === "first" ? selectable[0]
      : direction === "last" ? selectable[selectable.length - 1]
        : index === -1 ? nearestSelectable(from, direction)
          : selectable[Math.min(selectable.length - 1, Math.max(0, index + direction))];
    if (next === undefined || next === from) return;
    onChange(next);
    buttons.current.get(next)?.focus();
  }

  function onKeyDown(event, reps) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: "first", End: "last" };
    if (!(event.key in keys)) return;
    event.preventDefault();
    move(reps, keys[event.key]);
  }

  return (
    <HorizontalRail
      className="lz-insights-ep-roulette"
      role="radiogroup"
      aria-label="Rep count"
      snap="center"
      activeItem={value}
      scrollActiveIntoView={false}
      data-testid="ep-rep-roulette"
    >
      {repCounts.map((entry) => {
        const checked = entry.reps === value;
        const disabled = !entry.selectable;
        return (
          <button
            key={entry.reps}
            ref={(node) => {
              if (node) buttons.current.set(entry.reps, node);
              else buttons.current.delete(entry.reps);
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-disabled={disabled || undefined}
            aria-label={`${entry.reps} reps, ${entry.sessionCount} ${entry.sessionCount === 1 ? "session" : "sessions"}`}
            tabIndex={entry.reps === tabStop ? 0 : -1}
            data-rail-item={entry.reps}
            className={`lz-insights-ep-roulette__option${checked ? " lz-insights-ep-roulette__option--selected" : ""}${disabled ? " lz-insights-ep-roulette__option--disabled" : ""}`}
            onClick={() => {
              if (!disabled && !checked) onChange(entry.reps);
            }}
            onKeyDown={(event) => onKeyDown(event, entry.reps)}
          >
            <span className="lz-insights-ep-roulette__reps">{checked ? `${entry.reps} REPS` : entry.reps}</span>
            <span className="lz-insights-ep-roulette__count" aria-hidden="true">{entry.sessionCount}</span>
          </button>
        );
      })}
    </HorizontalRail>
  );
}
